<template>
  <div>
    <Feedback level="danger" icon simple v-if="hasNoSlot && !hasSlotsSearchError">
      <p>{{ $t('appointment.slots.noDateAvailable') }}</p>
    </Feedback>
    <template>
      <RadioButtonList :loading="loadingSlots" :required="required"
        v-if="hasSlots || otherDateOption || hasNoSlot || hasSlotsSearchError" v-model="selection">
        <option :key="`slot-${slot.key}`" :value="slot.key" v-for="slot in slots"
          :class="{ selected: isSelected(slot) }">
          <template>
            <div class="col-xs col-md-10 lib__text-left">
              {{ $t('dayOfWeek')[calendarService.daysOfWeek[slot.startDate.getDay()]] }} &nbsp;
              {{ dateService.displayDateText(slot.startDate) }}
              <div v-if="isSelected(slot)">
                {{ value.timeslotLabel }}
              </div>
            </div>
            <div class="col-xs col-md-2 lib__text-center" v-if="isSelected(slot)" @click.stop.prevent="deleteDate">
              <i class="fas fa-trash-alt"></i>
            </div>
          </template>
        </option>
        <option v-if="!showSeeMoreDates || hasNoSlot || hasSlotsSearchError" key="other-date" value="other-date"
          :class="{ selected: selection == 'other-date' }">
          <template>
            <div class="col-xs col-md-10 lib__text-left">
              {{ $t('appointment.slots.noDateWanted') }}
            </div>
            <div class="col-xs col-md-2 lib__text-center" v-if="selection == 'other-date'"
              @click.stop.prevent="deleteDate">
              <i class="fas fa-trash-alt"></i>
            </div>
          </template>
        </option>
      </RadioButtonList>
    </template>
    <p class="lib__text-center" v-if="showSeeMoreDates && !hasSlotsSearchError">
      <button @click.prevent="seeOtherDates" class="lib__btn lib__btn--outline">
        {{ $t('appointment.otherDates') }}
      </button>
    </p>
  </div>
</template>

<script lang="ts">
import Step from '@/components/steps/Step'
import Component from 'vue-class-component'
import { Prop, Emit, Watch } from 'vue-property-decorator'
import { calendarService, dateService, timeService, appointmentService } from '@/services'
import { AppointmentSlot } from '@/models'
import moment from 'moment'

@Component({
  name: 'AppointmentSlots'
})
export default class AppointmentSlots extends Step {
  //#region [Property]
  @Prop({ type: Array, required: false }) public currentSlots!: AppointmentSlot[] | null
  @Prop({ type: Object }) public value!: AppointmentSlot | null
  @Prop({ type: Boolean, default: false }) public required!: boolean | null
  @Prop({ type: Boolean, default: false }) public otherDateOption!: boolean
  @Prop({ type: Boolean, default: false }) public hasSlotsSearchError!: boolean
  @Prop({ type: Boolean, default: false }) public loadingSlots!: boolean

  //#endregion

  //#region [Data]
  public dateService = dateService
  public calendarService = calendarService
  public timeService = timeService
  public appointmentService = appointmentService
  private datesPage = 1
  private nbDatesPerPage = 5

  //#endregion

  //#region [Computed]
  public get selection(): string | null {
    return this.value ? this.value.key : null
  }
  public set selection(value: string | null) {
    this.selectSlot(value)
  }

  public get showSeeMoreDates(): boolean {
    if (!this.aggregatedSlots)
      return false

    return this.aggregatedSlots.length > (this.nbDatesPerPage * this.datesPage)
  }

  private get aggregatedSlots(): AppointmentSlot[] | null {

    const map = new Map<string, AppointmentSlot>();

    if (this.currentSlots) {
      this.currentSlots.forEach(item => {
        const key = moment(item.startDate).format("YYYY-MM-DD")
        if (!map.has(key)) {
          map.set(key, item);
        }
      });
    }

    return Array.from(map.values());
  }


  private get pagedAggregatedSlots(): AppointmentSlot[] | null {
    return this.aggregatedSlots?.slice(0, this.nbDatesPerPage * this.datesPage) ?? null;
  }

  private get hasNoSlot(): boolean {
    return !!this.aggregatedSlots && this.aggregatedSlots.length === 0
  }

  private get hasSlots(): boolean {
    return !!this.aggregatedSlots && this.aggregatedSlots.length > 0
  }

  private get slots(): AppointmentSlot[] {
    return this.pagedAggregatedSlots || []
  }

  private get otherSlot(): AppointmentSlot {
    return {
      startDate: null,
      endDate: null,
      timeslotLabel: '',
      otherDate: true,
      key: 'other-date'
    }
  }
  //#endregion

  //#region [Emit]
  @Emit('input')
  private selectSlot(key: string | null): AppointmentSlot | null {
    if (key === 'other-date') {
      return this.otherSlot
    }
    return key ? this.aggregatedSlots!.find((s) => s.key === key)! : null
  }

  @Emit('delete')
  private deleteDate() {
    return true
  }

  //#endregion

  //#region [Watch]
  @Watch('currentSlots')
  public async onCurrentSlotsChanged() {
    this.datesPage = 1
  }
  //#endregion

  //#region [Method]
  private isSelected(slot: AppointmentSlot): boolean {
    return !!this.value && this.value.key === slot.key
  }

  private seeOtherDates(): void {
    this.datesPage++
  }
  //#endregion
}
</script>
<style>
.fa-trash-alt {
  color: var(--lib__theme_secondary);
}
</style>